import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { AlertTriangle, Check, X } from "lucide-react";
import { getNews, getUrgentiNonLette, markLetta } from "@/api/news";
import type { NewsDetail, NewsListItem } from "@/types/news";
import { CategoryBadge } from "./CategoryBadge";
import { useAuth } from "@/auth/useAuth";

const SESSION_PREFIX = "news_urgenti_shown_";

function sessionKey(ids: string[]): string {
  return SESSION_PREFIX + [...ids].sort().join("_");
}

export function NewsUrgentiModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState<NewsListItem[]>([]);
  const [current, setCurrent] = useState<NewsDetail | null>(null);
  const [idx, setIdx] = useState(0);
  const cancelled = useRef(false);
  const skKey = useRef<string>("");

  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.roles.length === 1 && user.roles[0] === "cliente") return;

    getUrgentiNonLette().then((urgenti) => {
      if (cancelled.current) return;
      if (urgenti.length === 0) return;
      const key = sessionKey(urgenti.map((u) => u.id));
      if (sessionStorage.getItem(key) === "1") return;
      skKey.current = key;
      setQueue(urgenti);
      setIdx(0);
      getNews(urgenti[0].id).then((detail) => {
        if (cancelled.current) return;
        setCurrent(detail);
      });
    });
  }, [user]);

  if (!current || queue.length === 0) return null;

  function close() {
    if (skKey.current) sessionStorage.setItem(skKey.current, "1");
    setQueue([]);
    setCurrent(null);
  }

  async function ackAndNext() {
    await markLetta(current!.id).catch(() => {});
    if (cancelled.current) return;
    const nextIdx = idx + 1;
    if (nextIdx >= queue.length) {
      close();
    } else {
      setIdx(nextIdx);
      const next = await getNews(queue[nextIdx].id);
      if (cancelled.current) return;
      setCurrent(next);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="bg-red-500 text-white px-6 py-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6" />
          <div className="flex-1">
            <div className="font-bold">Comunicazione urgente</div>
            <div className="text-xs opacity-90">
              {idx + 1} di {queue.length}
            </div>
          </div>
          <button onClick={close} aria-label="Chiudi">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <CategoryBadge categoria={current.categoria} />
            <span className="text-xs text-muted-foreground">
              {new Date(current.data_pubblicazione).toLocaleDateString("it-IT", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
          <h2 className="text-xl font-bold mb-3">{current.titolo}</h2>
          <div className="prose prose-sm max-w-none text-slate-700">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
              {current.corpo}
            </ReactMarkdown>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <button onClick={close} className="px-4 py-2 text-sm text-muted-foreground">
              Salta
            </button>
            <button
              onClick={() => {
                close();
                navigate(`/news/${current.id}`);
              }}
              className="px-4 py-2 text-sm border rounded hover:bg-slate-50"
            >
              Apri completa
            </button>
            <button
              onClick={ackAndNext}
              className="px-4 py-2 text-sm bg-brand text-white rounded font-medium inline-flex items-center gap-1.5"
            >
              <Check size={16} /> Ho letto
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
